import {Injectable, OnDestroy} from '@angular/core';
import {BrowserSessionInfo} from '@webmate-web-core/shared/types/browsersession';
import {AuditId, ConsoleSessionId, FindingId, ProjectId, TestRunId, UserId} from '@webmate-web-core/shared/types/ids';
import {
    ConsoleInfo,
    DeviceContext,
    WorkbenchActiveMode,
    WorkbenchActiveModePayload,
    WorkbenchContext,
    WorkbenchInitConfig,
    WorkbenchRef
} from '../types';
import {AuditActivity} from '@webmate-web-core/shared/services/audit.service';
import {BehaviorSubject, Observable, Subscription} from 'rxjs';
import {distinctUntilChanged, filter, map} from 'rxjs/operators';
import {PersistenceService} from '@webmate-web-core/shared/services/persistence.service';
import _ from 'lodash';
import {User} from '@webmate-web-core/shared/types/user/user';
import {FindingCreationData} from '@webmate-web-core/shared/types/testmgmt';
import {WorkbenchIpcService, WorkbenchMode} from '@webmate-web-core/shared/workbench-ipc';
import {UserCustomizationService} from '@webmate-web-core/shared/services/user-customization.service';

@Injectable({
  providedIn: 'root'
})
export class WorkbenchContextService implements OnDestroy {
    private readonly ACTIVE_MODE_KEY_PREFIX = 'wm.workbench.activeMode.';
    private readonly RECENT_MODES_KEY_PREFIX = 'wm.workbench.recentModes.';
    workbenchContext = new BehaviorSubject<WorkbenchContext | undefined>(undefined);
    private popoutSyncSubscription?: Subscription;
    /** Reference count of use cases holding the active-browser-session lock (in-memory, not persisted). */
    private expeditionLockCount = 0;

    constructor(private persistenceService: PersistenceService,
                private workbenchIpcService: WorkbenchIpcService,
                private userCustomizationService: UserCustomizationService) {
    }

    initializeContext(currentUser: User, initConfig: WorkbenchInitConfig) {
        const latestFinding: FindingId | undefined = this.persistenceService.getItem('wm.workbench.latestFinding');
        const activeExpeditionObject = this.persistenceService.getObjectItem(`wm.workbench.activeExpedition.${initConfig.projectId}`);
        const activeExpedition: BrowserSessionInfo | undefined = !!activeExpeditionObject ? activeExpeditionObject as BrowserSessionInfo : undefined;
        const activeMode = this.resolveInitialActiveMode(currentUser, initConfig);
        const activeAudit = initConfig.auditId || (activeMode.kind === 'audit' ? activeMode.payload.auditId : undefined);
        const activeActivity = activeAudit ? this.persistenceService.getObjectItem(`wm.workbench.activeActivity.${activeAudit}`) as AuditActivity : undefined;
        const context: WorkbenchContext = {
            project: initConfig.projectId,
            currentUser: currentUser,
            activeMode,
            isChildWorkbench: initConfig.isChildWorkbench || false,
            childWorkbenches: [],
            auditContext: {
                activeFinding: latestFinding,
                activeAudit,
                activeAuditActivity: activeActivity,
            },
            expeditionContext: {
                activeExpedition
            }
        };
        this.overrideContext(context);
        this.persistActiveMode(context.activeMode);
        this.recordRecentMode(context.activeMode.kind);
        this.recordRecentAudit(context.project, context.activeMode);

        this.popoutSyncSubscription?.unsubscribe();
        this.popoutSyncSubscription = this.workbenchIpcService.observeType('workbench:popout-sync').subscribe(message => {
            const payload = message.payload;
            const currentContext = this.getCurrentWorkbenchContext();
            if (!currentContext) {
                return;
            }
            if (message.projectId !== currentContext.project) {
                return;
            }
            if (currentContext.isChildWorkbench && payload.fromChild) {
                // ignore messages from child workbenches in popout mode
                return;
            }
            if (payload.syncType === 'update-context' && !payload.fromChild) {
                // context updates are currently not allowed from child workbenches. TODO: think about these workflows
                this.patchContext(payload.contextPatch as Partial<WorkbenchContext>, true);
            } else {
                console.warn('Unknown popout sync message received:', payload.syncType);
            }
        });
    }

    private makeSharableContext(context: WorkbenchContext): Object {
        return {
            activeFinding: context.auditContext.activeFinding,
            activeAudit: context.auditContext.activeAudit,
            activeMode: context.activeMode,
            activeExpedition: context.expeditionContext.activeExpedition,
            deviceContext: context.deviceContext,
            testRunContext: context.testRunContext
        } as Object;
    }

    overrideContext(context: WorkbenchContext){
        this.workbenchContext.next(context);
    }

    addChildWorkbench(workbenchRef: WorkbenchRef) {
        setTimeout(() => {
            this.updateContext({
                childWorkbenches: [...(this.workbenchContext.getValue()?.childWorkbenches || []), workbenchRef]
            }, () => {}, false);
        });
    }

    removeChildWorkbench(workbenchRef: WorkbenchRef) {
        setTimeout(() => {
            const currentContext = this.workbenchContext.getValue();
            if (!currentContext) {
                return;
            }
            const childWorkbenches = currentContext.childWorkbenches.filter(wb => wb !== workbenchRef);
            this.updateContext({
                childWorkbenches
            });
        });
    }

    private updateContext(patch: Partial<WorkbenchContext>, callback?: Function, dontBroadcast = false) {
        const context = this.workbenchContext.getValue();
        if (!context) {
            return;
        }
        setTimeout(() => {
            const currentContext = this.workbenchContext.getValue();
            if (!currentContext) {
                return;
            }
            const newContext = _.mergeWith(_.cloneDeep(currentContext), patch, (_objValue, srcValue) => {
                if (Array.isArray(srcValue)) {
                    return srcValue;
                }
                return undefined;
            });
            if (patch.activeMode) {
                newContext.activeMode = _.cloneDeep(patch.activeMode);
            }
            if (_.isEqual(currentContext, newContext)) {
                return;
            }
            this.workbenchContext.next(newContext);
            callback?.();

            if (!dontBroadcast) {
                this.broadcastContextUpdate();
            }
        });
    }

    private broadcastContextUpdate() {
        const context = this.workbenchContext.getValue();
        if (!context) {
            return;
        }

        const sharableContext = this.makeSharableContext(context);

        this.workbenchIpcService.popoutSync({
            syncType: 'update-context',
            fromChild: context.isChildWorkbench,
            contextPatch: sharableContext as Record<string, unknown>
        }, context.project, undefined, context.isChildWorkbench ? 'popout' : 'workbench');
    }

    private patchContext(patch: Partial<WorkbenchContext>, dontBroadcast = false) {
        this.updateContext(patch, undefined, dontBroadcast);
    }

    setActiveMode(activeMode: WorkbenchActiveMode, dontBroadcast = false): void {
        const context = this.workbenchContext.getValue();
        if (!context) {
            return;
        }
        const clearsManualTestContext = activeMode.kind === 'default' && !!context.testRunContext?.activeTestRun;
        const auditContextPatch = this.auditContextPatchForActiveMode(activeMode, context);
        if (_.isEqual(context.activeMode, activeMode) && !clearsManualTestContext && !auditContextPatch) {
            return;
        }
        const patch: Partial<WorkbenchContext> = {
            activeMode,
            ...(auditContextPatch ? {auditContext: auditContextPatch} : {}),
            ...(activeMode.kind === 'default'
                ? {testRunContext: {activeTestRun: null}}
                : {})
        };
        this.persistActiveMode(activeMode);
        this.recordRecentMode(activeMode.kind);
        this.recordRecentAudit(context.project, activeMode);
        this.updateContext(patch, undefined, dontBroadcast);
    }

    activateMode(mode: WorkbenchMode, payload?: WorkbenchActiveModePayload, dontBroadcast = false): void {
        this.setActiveMode(this.activeModeFromKind(mode, payload), dontBroadcast);
    }

    clearActiveMode(dontBroadcast = false): void {
        this.setActiveMode({kind: 'default'}, dontBroadcast);
    }

    setActiveModeKind(mode: WorkbenchMode, dontBroadcast = false): void {
        this.activateMode(mode, undefined, dontBroadcast);
    }

    observeActiveMode$(): Observable<WorkbenchActiveMode> {
        return this.subscribeWorkbenchContext$().pipe(
            map(context => context.activeMode),
            distinctUntilChanged((a, b) => _.isEqual(a, b))
        );
    }

    getActiveMode(): WorkbenchActiveMode | undefined {
        return this.workbenchContext.getValue()?.activeMode;
    }

    getRecentModes(): WorkbenchMode[] {
        const value = this.persistenceService.getObjectItem(this.getRecentModesKey());
        return Array.isArray(value) ? value.filter(mode => this.isWorkbenchMode(mode)) : [];
    }

    /**
     * Make the specified console info active in the workbench context.
     */
    setContextActiveConsole(consoleInfo: ConsoleInfo | undefined) {
        this.updateContext({
            deviceContext: {
                ...this.prepareConsoleContext(!!consoleInfo ? consoleInfo : null)
            }
        });
    }

    /**
     * Remove the specified console info in the workbench context.
     */
    setContextRemoveOpenedConsole(consoleSessionId: ConsoleSessionId) {
        const context = this.workbenchContext.getValue();
        const active = context?.deviceContext?.activeConsole;
        const removingActiveConsole = !!active && active.consoleSession == consoleSessionId;
        this.updateContext({
            deviceContext: {
                // Clear with null, not undefined: updateContext merges via _.mergeWith, which skips
                // undefined source values — so undefined would leave the stale value in place.
                activeConsole: removingActiveConsole ? null : active,
                // Closing the active console also clears the active device, so the deployed-devices view
                // does not keep highlighting a device whose console is gone. (Re-set if another console
                // becomes active.) When a non-active console is removed, leave activeDevice untouched.
                ...(removingActiveConsole ? { activeDevice: null } : {}),
                openedConsoles: (() => {
                    const existing: ConsoleInfo[] = (() => {
                        if (context?.deviceContext?.openedConsoles) {
                            return context!.deviceContext!.openedConsoles!;
                        }
                        return [];
                    })();
                    return existing.filter(c => c.consoleSession !== consoleSessionId);
                })(),
            }
        });
    }

    prepareConsoleContext = (consoleInfo: ConsoleInfo | undefined) =>  {
        const context = this.workbenchContext.getValue();
        return {
            activeConsole: consoleInfo ?? null,
            openedConsoles: (() => {
                const existing: ConsoleInfo[] = (() => {
                    if (context?.deviceContext?.openedConsoles) {
                        return context!.deviceContext!.openedConsoles!;
                    }
                    return [];
                })();
                if (!consoleInfo) {
                    return existing;
                }
                return [...new Set([consoleInfo, ...existing])] as ConsoleInfo[];
            })(),
        };
    }

    setContextActiveDeviceContext(deviceContext: DeviceContext) {
        const context: DeviceContext = {
            activeDevice: deviceContext.activeDevice ?? null,
            ...this.prepareConsoleContext(deviceContext.activeConsole),
        };
        this.patchContext({deviceContext: context});
    }

    setContextActiveFinding(finding: FindingId | undefined) {
        this.updateContext({
            auditContext: {
                activeFinding: !!finding ? finding : null
            }
        });
        // Clearing the active finding also drops the persisted reference so a deleted finding is not restored
        // again on the next workbench load. The persisted id is written by the finding view once a finding has
        // been successfully shown, so we only manage the removal side here to stay consistent with the
        // expedition and audit-activity setters.
        if (!finding) {
            this.persistenceService.removeItem('wm.workbench.latestFinding');
        }
    }

    setContextPseudoFinding(creationData: FindingCreationData | undefined) {
        this.updateContext({
            pseudoFinding: !!creationData ? creationData : null
        });
    }

    setContextActiveAuditActivity(activity: AuditActivity | undefined): void {
        this.updateContext({
            auditContext: {
                activeAuditActivity: !!activity ? activity : null
            }
        });
        if (!!activity) {
            this.persistenceService.setObjectItem(`wm.workbench.activeActivity.${this.getCurrentWorkbenchContext().auditContext?.activeAudit}`, activity);
        } else {
            this.persistenceService.removeItem(`wm.workbench.activeActivity.${this.getCurrentWorkbenchContext().auditContext?.activeAudit}`);
        }
    }

    setContextActiveExpedition(expedition: BrowserSessionInfo | undefined) {
        // Any change of the active session releases the lock (e.g. a dead-session clear); the new active
        // session starts unlocked and resumes following the active console.
        this.expeditionLockCount = 0;
        this.updateContext({
            expeditionContext: {
                activeExpedition: !!expedition ? expedition : null,
                activeExpeditionLocked: false
            }
        });
        if (!!expedition) {
            this.persistenceService.setObjectItem(`wm.workbench.activeExpedition.${this.getProjectId()}`, expedition);
        } else {
            this.persistenceService.removeItem(`wm.workbench.activeExpedition.${this.getProjectId()}`);
        }
    }

    /**
     * Lock the active browser session for the duration of a use case (e.g. a running analysis), so it no
     * longer follows the active console and cannot be changed until the use case releases it. The lock is
     * reference-counted: concurrent use cases each hold it. No-op when there is no active session. The lock
     * is in-memory and not persisted. There is no manual unlock — see {@link unlockActiveExpedition}.
     */
    lockActiveExpedition() {
        if (!this.workbenchContext.getValue()?.expeditionContext?.activeExpedition) {
            return;
        }
        this.expeditionLockCount++;
        if (!this.workbenchContext.getValue()?.expeditionContext?.activeExpeditionLocked) {
            this.updateContext({
                expeditionContext: {
                    activeExpeditionLocked: true
                }
            });
        }
    }

    /**
     * Release one hold of the active-session lock. When the last hold is released, the session is unlocked
     * and resumes following the active console. Safe to call when not locked (no-op).
     */
    unlockActiveExpedition() {
        if (this.expeditionLockCount === 0) {
            return;
        }
        this.expeditionLockCount--;
        if (this.expeditionLockCount === 0 && this.workbenchContext.getValue()?.expeditionContext?.activeExpeditionLocked) {
            this.updateContext({
                expeditionContext: {
                    activeExpeditionLocked: false
                }
            });
        }
    }

    isActiveExpeditionLocked(): boolean {
        return !!this.workbenchContext.getValue()?.expeditionContext?.activeExpeditionLocked;
    }

    setContextActiveTestRun(testRunId: TestRunId | undefined) {
        this.updateContext({
            testRunContext: {
                activeTestRun: !!testRunId ? testRunId : null
            }
        })
    }

    getCurrentWorkbenchContext(): WorkbenchContext | undefined {
        return this.workbenchContext.getValue();
    }

    getProjectId(): ProjectId {
        const context = this.workbenchContext.getValue();
        if (!context) {
            throw new Error('No workbench context available');
        }
        return context.project;
    }

    getUserId(): UserId {
        const context = this.workbenchContext.getValue();
        if (!context) {
            throw new Error('No workbench context available');
        }
        return context.currentUser.id;
    }

    /**
     * Subscribe to the workbench context, which gives you updates about the currently active project, audit, testrun, expedition, etc.
     */
    subscribeWorkbenchContext$(): Observable<WorkbenchContext> {
        return this.workbenchContext.asObservable().pipe(filter((c): c is WorkbenchContext=> !!c));
    }

    ngOnDestroy(): void {
        this.popoutSyncSubscription?.unsubscribe();
    }

    private getInitialActiveMode(initConfig: WorkbenchInitConfig): WorkbenchActiveMode {
        // Mode is derived strictly from workflow anchors. The legacy `?activeMode=` URL parameter is no
        // longer accepted because it could outlive the workflow it referenced and produce stale UI.
        if (initConfig.auditId) {
            return {
                kind: 'audit',
                payload: {
                    auditId: initConfig.auditId
                }
            };
        }
        if (initConfig.legacyProfile === 'audit') {
            return {
                kind: 'audit',
                payload: {
                    auditId: initConfig.auditId
                }
            };
        }
        if (initConfig.activeTestRun || initConfig.autoStartManualTest !== undefined || initConfig.suppressAutoResumeTestRun) {
            return {
                kind: 'manual-test',
                payload: {
                    autoStart: initConfig.autoStartManualTest,
                    suppressAutoResume: initConfig.suppressAutoResumeTestRun
                }
            };
        }
        return {kind: 'default'};
    }

    private resolveInitialActiveMode(currentUser: User, initConfig: WorkbenchInitConfig): WorkbenchActiveMode {
        const startupMode = this.getInitialActiveMode(initConfig);
        if (this.hasExplicitStartupMode(initConfig)) {
            return startupMode;
        }
        return this.readPersistedActiveMode(initConfig.projectId, currentUser.id) || startupMode;
    }

    private hasExplicitStartupMode(initConfig: WorkbenchInitConfig): boolean {
        return !!initConfig.auditId ||
            initConfig.legacyProfile === 'audit' ||
            !!initConfig.activeTestRun ||
            initConfig.autoStartManualTest !== undefined ||
            !!initConfig.suppressAutoResumeTestRun ||
            this.isPlainDefaultStartup(initConfig);
    }

    private isPlainDefaultStartup(initConfig: WorkbenchInitConfig): boolean {
        return (!!initConfig.deviceId || !!initConfig.browserExpeditionStartConfig) &&
            !initConfig.auditId &&
            initConfig.legacyProfile !== 'audit' &&
            !initConfig.activeTestRun &&
            initConfig.autoStartManualTest === undefined &&
            !initConfig.suppressAutoResumeTestRun;
    }

    activeModeFromKind(mode: WorkbenchMode, payload?: WorkbenchActiveModePayload): WorkbenchActiveMode {
        switch (mode) {
            case 'audit':
                return {
                    kind: 'audit',
                    payload: payload && 'auditId' in payload ? {auditId: payload.auditId} : this.activeAuditModePayloadFromContext()
                };
            case 'manual-test':
                return {
                    kind: 'manual-test',
                    payload: payload && ('autoStart' in payload || 'suppressAutoResume' in payload)
                        ? {
                            autoStart: payload.autoStart,
                            suppressAutoResume: payload.suppressAutoResume
                        }
                        : undefined
                };
            case 'default':
                return {kind: 'default'};
        }
    }

    private activeAuditModePayloadFromContext(): {auditId?: string} {
        const activeAudit = this.workbenchContext.getValue()?.auditContext?.activeAudit;
        return activeAudit ? {auditId: activeAudit} : {};
    }

    private auditContextPatchForActiveMode(activeMode: WorkbenchActiveMode, context: WorkbenchContext): WorkbenchContext['auditContext'] | undefined {
        if (activeMode.kind !== 'audit') {
            // Leaving audit mode: clear the stale audit context so that subsequent finding creation
            // targets the active test run instead of the previously active audit.
            const auditContext = context.auditContext;
            if (auditContext?.activeAudit || auditContext?.activeAuditActivity || auditContext?.activeFinding) {
                return {
                    activeAudit: null,
                    activeAuditActivity: null,
                    activeFinding: null
                };
            }
            return undefined;
        }
        if (!activeMode.payload?.auditId || context.auditContext?.activeAudit === activeMode.payload.auditId) {
            return undefined;
        }
        const activeAudit = activeMode.payload.auditId;
        const activeAuditActivity = this.persistenceService.getObjectItem(`wm.workbench.activeActivity.${activeAudit}`) as AuditActivity | undefined;
        return {
            activeFinding: context.auditContext?.activeFinding,
            activeAudit,
            activeAuditActivity
        };
    }

    private recordRecentMode(mode: WorkbenchMode): void {
        const recentModes = [mode, ...this.getRecentModes().filter(existingMode => existingMode !== mode)].slice(0, 5);
        this.persistenceService.setObjectItem(this.getRecentModesKey(), recentModes);
    }

    private recordRecentAudit(projectId: ProjectId, activeMode: WorkbenchActiveMode): void {
        if (activeMode.kind === 'audit' && activeMode.payload?.auditId) {
            this.userCustomizationService.updateMostRecentAudit(projectId, activeMode.payload.auditId);
        }
    }

    private persistActiveMode(activeMode: WorkbenchActiveMode): void {
        this.persistenceService.setObjectItem(this.getActiveModeKey(), this.normalizeActiveModeForPersistence(activeMode));
    }

    private readPersistedActiveMode(projectId: ProjectId, userId: UserId): WorkbenchActiveMode | undefined {
        return this.normalizePersistedActiveMode(this.persistenceService.getObjectItem(this.getActiveModeKey(projectId, userId)));
    }

    private normalizeActiveModeForPersistence(activeMode: WorkbenchActiveMode): WorkbenchActiveMode {
        if (activeMode.kind === 'audit') {
            return {
                kind: 'audit',
                payload: {
                    auditId: activeMode.payload.auditId
                }
            };
        }
        return {kind: activeMode.kind};
    }

    private normalizePersistedActiveMode(value: unknown): WorkbenchActiveMode | undefined {
        if (!value || typeof value !== 'object' || !('kind' in value)) {
            return undefined;
        }

        const mode = value as WorkbenchActiveMode;
        if (mode.kind === 'default') {
            return {kind: 'default'};
        }
        if (mode.kind === 'manual-test') {
            return {kind: 'manual-test'};
        }
        if (mode.kind === 'audit') {
            const auditId = mode.payload?.auditId;
            return auditId ? {kind: 'audit', payload: {auditId}} : undefined;
        }
        return undefined;
    }

    private getActiveModeKey(projectId = this.getProjectId(), userId = this.getUserId()): string {
        return `${this.ACTIVE_MODE_KEY_PREFIX}${projectId}.${userId}`;
    }

    private getRecentModesKey(projectId = this.getProjectId(), userId = this.getUserId()): string {
        return `${this.RECENT_MODES_KEY_PREFIX}${projectId}.${userId}`;
    }

    private isWorkbenchMode(value: unknown): value is WorkbenchMode {
        return value === 'default' || value === 'manual-test' || value === 'audit';
    }
}
